import React, { useContext, useEffect, useState } from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import ReactHtmlParser from 'react-html-parser';

import FirebaseContext from '../../context/FirebaseContext';
import UserContext, { withUserContextReady } from '../../context/UserContext';
import useStorage from '../../hooks/useStorage';
import useComponentMounted from '../../hooks/useComponentMounted';
import useNavigation from '../../hooks/useNavigation';
import { withPageAnalytics } from '../../components/withAnalytics';
import withRedirectToApp from '../../components/withRedirectToApp';
import DownloadApp from '../../components/DownloadApp';
import { withUserOnboarded } from '../../components/withUserOnboarded';
import LoadingPage from '../../components/LoadingPage';
import {
  CommitmentStickerFlow,
  EntryPoint,
  ShareCardStatus,
  useShareCard,
} from '../../components/StoryShare';
import ShareASale from '../Onboarding/Subscription/components/ShareASale';
import Coach, { ColorModes } from '../../Model/Coach';
import AppCustomization from '../../CoachTools/Model/AppCustomization';

import LightAppPreview from '../../assets/light-app-preview.png';
import DarkAppPreview from '../../assets/dark-app-preview.png';

import {
  Container,
  HeaderContainer,
  StyledAppIcon,
  UserDetailsSection,
  LogoutButton,
  ContentContainer,
  LeftSideWrapper,
  RightSideWrapper,
  StyledAppPreview,
  Title,
  DownloadText,
  ChecklistContainer,
  ChecklistTitle,
  ChecklistItem,
  NextSteps,
  UserName,
  StyledCheckIcon,
} from './styles';

import texts from './texts.json';

const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { firebase } = useContext(FirebaseContext);
  const { userDoc } = useContext(UserContext);
  const {
    state: {
      color,
      isDarkTheme,
      appIconUrl: coachIconURL,
      storyShare,
      // Arriving straight from a purchase at checkout, which this page reports to ShareASale.
      isNewPurchase,
    } = {
      color: null,
      isDarkTheme: null,
      appIconUrl: null,
      isNewPurchase: false,
    },
  } = useNavigation();
  // Set only on the way in from a challenge checkout, so the sticker is offered once, right after paying.
  const { status: shareCardStatus, card: shareCard } = useShareCard(storyShare?.challengeId);
  const { getBlobUrl } = useStorage();
  const isComponentMountedRef = useComponentMounted();

  const [isCheckoutPageDark, setIsCheckoutPageDark] = useState(isDarkTheme);
  const [coachAppIconUrl, setCoachAppIconUrl] = useState(coachIconURL);
  const [coachPrimaryColor, setCoachPrimaryColor] = useState(color);

  useEffect(() => {
    const init = async () => {
      if (!color || !isDarkTheme || !coachIconURL) {
        const coachDoc = await Coach.getCoachById(userDoc.assignedCoach);
        const appCustomization = await AppCustomization.getAppCustomizationByCoach(userDoc.assignedCoach);
        // Some coaches have not configured a web app icon, in which case webAppIconRef is undefined.
        // Passing undefined to getBlobUrl reaches storage.ref().child(undefined) and throws in the
        // Firebase Storage SDK ("Cannot read properties of undefined (reading 'split')"), which aborts
        // the home screen render. Skip the lookup and fall back to no icon instead.
        const appIconUrl = appCustomization?.webAppIconRef
          ? await getBlobUrl(appCustomization.webAppIconRef)
          : null;
        if (isComponentMountedRef.current) {
          setCoachPrimaryColor(appCustomization?.primaryColor);
          setCoachAppIconUrl(appIconUrl);
          setIsCheckoutPageDark(coachDoc.checkoutPageColorMode !== ColorModes.LIGHT);
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    if (userDoc.assignedCoach) {
      init();
    }
  }, [
    userDoc.assignedCoach,
    getBlobUrl,
    isComponentMountedRef,
    isDarkTheme,
    coachIconURL,
    color,
  ]);

  const signOut = async () => {
    await firebase.signOut();
  };

  if (isLoading || shareCardStatus === ShareCardStatus.LOADING) {
    return <LoadingPage />;
  }

  const showStory = shareCardStatus === ShareCardStatus.READY;

  return (
    <Container $isDarkTheme={isCheckoutPageDark}>
      {!!isNewPurchase && <ShareASale userId={userDoc.id} />}
      <HeaderContainer>
        {!!coachAppIconUrl && <StyledAppIcon src={coachAppIconUrl} />}
        <UserDetailsSection>
          <UserName>
            {userDoc.name}
          </UserName>
          <LogoutButton
            onClick={signOut}
            $isDarkTheme={isCheckoutPageDark}
          >
            {texts.logOut}
          </LogoutButton>
        </UserDetailsSection>
      </HeaderContainer>
      {showStory ? (
        <CommitmentStickerFlow
          card={shareCard}
          entryPoint={EntryPoint.CHECKOUT}
          addedCoaching={storyShare.addedCoaching}
        />
      ) : (
        <ContentContainer>
          <LeftSideWrapper $isDarkTheme={isCheckoutPageDark}>
            <StyledAppPreview
              src={isCheckoutPageDark ? DarkAppPreview : LightAppPreview}
              alt="App Preview"
            />
          </LeftSideWrapper>
          <RightSideWrapper $isDarkTheme={isCheckoutPageDark}>
            <Title
              $background={coachPrimaryColor}
              $isDarkTheme={isCheckoutPageDark}
            >
              {texts.thankYouForPayment}
            </Title>
            <DownloadText $isDarkTheme={isCheckoutPageDark}>{texts.downloadApp}</DownloadText>
            <ChecklistContainer $isDarkTheme={isCheckoutPageDark}>
              <ChecklistTitle>{texts.whatNeedToDoNext}</ChecklistTitle>
              <ChecklistItem $isDarkTheme={isCheckoutPageDark}>
                <StyledCheckIcon />
                <NextSteps $isDarkTheme={isCheckoutPageDark}>{ReactHtmlParser(texts.downloadAppUsingLink)}</NextSteps>
              </ChecklistItem>
              <ChecklistItem $isDarkTheme={isCheckoutPageDark}>
                <StyledCheckIcon />
                <NextSteps $isDarkTheme={isCheckoutPageDark}>{ReactHtmlParser(texts.openAppAndLogin)}</NextSteps>
              </ChecklistItem>
              <ChecklistItem $isDarkTheme={isCheckoutPageDark}>
                <StyledCheckIcon />
                <NextSteps $isDarkTheme={isCheckoutPageDark}>{ReactHtmlParser(texts.completeOnboarding)}</NextSteps>
              </ChecklistItem>
            </ChecklistContainer>
            <DownloadApp />
          </RightSideWrapper>
        </ContentContainer>
      )}
    </Container>
  );
};

export default compose(
  withPageAnalytics('homePage'),
  withRedirectToApp,
  withUserContextReady,
  withUserOnboarded,
  observer,
)(Home);
